/**
 * `MEDAL.DAT`'s twelve citations, transcribed, and the expansion of their five
 * tokens.
 *
 * The original does not compose a citation any more than it composes a
 * debriefing: it picks the medal's stored template out of `MEDAL.DAT` and fills
 * five slots. So this module is the same shape `debrief-review-text.ts` is — the
 * corpus checked in so the game plays with no volume mounted, and
 * `tools/bake/test/ceremony-text.test.ts` asserting it line-for-line against
 * `packages/formats`' `decodeMedalDat` reading the real `VOLUME.006` bytes. If
 * the two ever disagree, the bake test fails; there is no second copy to drift.
 *
 * | Token | Fills with |
 * |---|---|
 * | `%n` | the pilot's name |
 * | `%m` | the medal's name — **`MEDAL.DAT`'s own**, see below |
 * | `%k` | confirmed victories |
 * | `%w` | the decree line, `PS.EXE` verbatim, by nation |
 * | `%b` | the bar suffix, `PS.EXE` verbatim |
 *
 * Three things the file settles that the port had guessed wrong:
 *
 * - **The decree line is not on every citation.** `%w` appears in exactly three
 *   templates — the Pour le Merite, the same with Oakleaf, and the Victoria
 *   Cross — the three "highest honour" awards. The port used to append *"by
 *   Imperial Decree, …"* to all nine of its medals, so a first Iron Cross came
 *   with the Emperor's personal decree.
 * - **`%b` is on exactly the three decorations that take bars** — M.C., D.F.C.,
 *   D.S.O. `medals.ts` reached the same three from the manual's Medals and
 *   Awards table, an entirely separate source, and the two agreeing is
 *   independent evidence for both.
 * - **`%n` is the pilot's name, not his rank and name.** Every other `%n` in
 *   `PS.EXE` — *"%n was killed in action today"*, *"In %d, %n was grounded…"* —
 *   is the bare name, and nothing in the file adds a title. The port used to
 *   write *"Captain A. Cooper is awarded…"*; that title was ours.
 *
 * **`%m` is `MEDAL.DAT`'s name, which is not always `medals.ts`'s name.** The
 * roster in `medals.ts` is verbatim from the manual (`Order for Merit / "The
 * Blue Max"`), and this file is verbatim from the game (`Orden Pour le Merite`).
 * Both are the original's own words from different documents; the citation is
 * the game's sentence, so it uses the game's name, and the pilot record keeps
 * the manual's. `MEDAL_FILE_ABBREVIATIONS` is the join between them.
 */

import type { Nation } from "../mission/mission-roster.ts";
import { BAR_SUFFIXES, CITATION_AUTHORITY } from "./medals.ts";

export interface MedalCitation {
  /** `MEDAL.DAT`'s second run: `"IC2"`, `"OM"`, `"VC"`. The stable key. */
  readonly abbreviation: string;
  /** `MEDAL.DAT`'s first run. What `%m` expands to. */
  readonly name: string;
  /** The stored citation lines, tokens unexpanded, in file order. */
  readonly lines: readonly string[];
}

/** All twelve, in `MEDAL.DAT`'s own order — which is `MEDALS.BMP`'s frame order too. */
export const MEDAL_CITATIONS: readonly MedalCitation[] = [
  {
    abbreviation: "IC2",
    name: "Iron Cross 2nd Class",
    lines: ["For meritorious service for the German Empire", "%n is awarded the %m."],
  },
  {
    abbreviation: "IC1",
    name: "Iron Cross 1st Class",
    lines: [
      "For distinguished service for the German Empire,",
      "against the enemies of the Fatherland,",
      "%n is awarded the %m.",
    ],
  },
  {
    abbreviation: "KC",
    name: "Royal Hohenzollern House Order",
    lines: [
      "In recognition of continued bravery against the enemies",
      "of the German Empire, in that he has single-handedly",
      "destroyed %k enemy aircraft, %n is awarded the %m.",
    ],
  },
  {
    abbreviation: "OM",
    name: "Orden Pour le Merite",
    lines: [
      "For courage of the highest order on the battlefield",
      "and for exceptional service for the German Empire,",
      "in that he has single-handedly destroyed %k enemy",
      "aircraft, %n is awarded Prussia's highest military",
      "order, the %m, %w",
    ],
  },
  {
    abbreviation: "RE",
    name: "The Red Eagle Order",
    lines: [
      "For continued distinguished service for the",
      "German Empire in the face of the enemy,",
      "in that he has single-handedly destroyed %k",
      "enemy aircraft, %n is awarded the %m.",
    ],
  },
  {
    abbreviation: "OMO",
    name: "Orden Pour le Merite with Oakleaf",
    lines: [
      "For continued and consistent bravery of the highest order,",
      "in that he has won many battles against the enemies of the",
      "Fatherland, and in that he has destroyed one-hundred enemy",
      "aircraft, and is the pre-eminent ace of the war,",
      "%n is awarded Prussia's highest military order,",
      "the %m, %w",
    ],
  },
  {
    abbreviation: "MC",
    name: "Military Cross",
    lines: ["In recognition of meritorious service", "in time of war, %n is hereby awarded the %m%b."],
  },
  {
    abbreviation: "DFC",
    name: "Distinguished Flying Cross",
    lines: [
      "For courage and devotion to duty while flying",
      "in numerous missions against the enemy,",
      "%n is awarded the %m%b.",
    ],
  },
  {
    abbreviation: "DSO",
    name: "Distinguished Service Order",
    lines: [
      "For distinguished service for the British Empire",
      "and for valor in the face of the enemy,",
      "%n is awarded the %m%b.",
    ],
  },
  {
    abbreviation: "VC",
    name: "Victoria Cross",
    lines: [
      "For bravery in the presence of the enemy,",
      "%n is awarded Great Britain's highest honor,",
      "the %m, %w",
    ],
  },
  {
    abbreviation: "CG",
    name: "Croix de Guerre",
    lines: ["For meritorious service against the enemies of France,", "%n is awarded the %m."],
  },
  {
    abbreviation: "LH",
    name: "Legion of Honor",
    lines: [
      "In recognition of outstanding service against",
      "the enemies of France, %n is awarded",
      "France's highest honor, the %m.",
    ],
  },
];

/**
 * `medals.ts`'s roster ids against `MEDAL.DAT`'s abbreviations. Nine of the
 * twelve; `UNCITED_MEDALS` records the other three rather than dropping them.
 */
export const MEDAL_FILE_ABBREVIATIONS: Readonly<Record<string, string>> = {
  "iron-cross-2": "IC2",
  "iron-cross-1": "IC1",
  hohenzollern: "KC",
  "blue-max": "OM",
  "red-eagle": "RE",
  "military-cross": "MC",
  dfc: "DFC",
  dso: "DSO",
  "victoria-cross": "VC",
};

/**
 * Citations in the file that no career pilot can be read. An enumerable register
 * rather than a silent omission, matching `medals.ts`'s `UNEARNABLE_MEDALS`.
 */
export const UNCITED_MEDALS: readonly { readonly abbreviation: string; readonly reason: string }[] = [
  {
    abbreviation: "OMO",
    reason:
      "The Pour le Merite with Oakleaf. Its own citation says what it takes — \"destroyed one-hundred enemy aircraft, and is the pre-eminent ace of the war\" — and no threshold that high is in the port's roster; only Richthofen's 80 and Fonck's 75 are named anywhere, both below it.",
  },
  {
    abbreviation: "CG",
    reason: "French. Career mode enlists only in the R.F.C. or the German Air Service — see medals.ts's UNEARNABLE_MEDALS.",
  },
  { abbreviation: "LH", reason: "French, and the same reason." },
];

export function citationByAbbreviation(abbreviation: string): MedalCitation | undefined {
  return MEDAL_CITATIONS.find((entry) => entry.abbreviation === abbreviation);
}

/** The citation `medals.ts`'s medal id is announced with, or nothing for the three uncited. */
export function citationForMedal(medalId: string): MedalCitation | undefined {
  const abbreviation = MEDAL_FILE_ABBREVIATIONS[medalId];
  return abbreviation === undefined ? undefined : citationByAbbreviation(abbreviation);
}

/** `MEDALS.BMP`'s frame for a citation — the sheet's first twelve frames are these twelve medals. */
export function medalPictureFrame(citation: MedalCitation): number {
  return MEDAL_CITATIONS.indexOf(citation);
}

export interface CitationFill {
  /** `%n`. */
  readonly pilot: string;
  /** `%k`. */
  readonly victories: number;
  /** Which nation's decree line `%w` takes. */
  readonly nation: Nation;
  /** How many bars the award carries, for `%b`. 0 is the plain award. */
  readonly bars: number;
}

/**
 * Fills one citation's five slots. `%w` and `%b` come from `medals.ts`, which
 * already held both `PS.EXE` tables for the award display name — there is one
 * copy of each string in the port, not two.
 *
 * Note what `%w` does **not** end with: the templates read `"the %m, %w"` and
 * `PS.EXE`'s decree strings carry no full stop, so neither does the drawn line.
 * That is the file's own punctuation, not an omission.
 */
export function expandCitation(citation: MedalCitation, fill: CitationFill): readonly string[] {
  const bar = BAR_SUFFIXES[Math.min(fill.bars, BAR_SUFFIXES.length - 1)] ?? "";
  return citation.lines.map((line) =>
    line
      .replaceAll("%n", fill.pilot)
      .replaceAll("%m", citation.name)
      .replaceAll("%b", bar)
      .replaceAll("%k", String(fill.victories))
      .replaceAll("%w", CITATION_AUTHORITY[fill.nation]),
  );
}
